import {Button, Grid, Table, TableBody, TableCell, TableRow} from "@mui/material";
import {orderCellStyles, supplyCellStyles, cellStyle, tableInRowStyle, supplyProductStyles} from "./supplyTableStyles";
import React, {useCallback, useMemo, useState, useEffect, useRef} from "react";
import SupplyDetailTable from "./SupplyDetailTable";
import CommonAutocomplete from "../../../../components/CommonAutocomplete";
import {red, green} from "@mui/material/colors";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useDrag, useDrop } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";

export const DND_ITEM_TYPES = {
    PRODUCT_ROW: "PRODUCT_ROW",
    HIDDEN_ROW: "HIDDEN_ROW",
    ADD_PRODUCT_ROW: "ADD_PRODUCT_ROW",
};

function SupplyTableRow({
                            supplyForm,
                            originalForm,
                            index,
                            visibleIndex,
                            suppliers,
                            changeSupplyQuantityDetailsAt,
                            enabledDisplayRemarks,
                            keyHandler,
                            origins,
                            onOriginChange,
                            onHideProduct,
                            onMoveItem,
                            onShowProductAt,
                            onGhostHover,
                            isRecentlyDropped,
                        }) {

    const supplyFormMemo = useMemo(() => supplyForm, [supplyForm.supplyQuantityDetails])
    const [origin, setOrigin] = useState({});

    useEffect(() => {
        if (supplyForm.originId) {
            setOrigin(origins.find((x) => x.originId === supplyForm.originId));
        } else {
            setOrigin({});
        }
    }, [supplyForm.originId,origins]);

    const changeSupplyDetails = useCallback(
        changeSupplyQuantityDetailsAt(supplyFormMemo.supplyProductIndex)
        , [changeSupplyQuantityDetailsAt, supplyFormMemo.supplyProductIndex]);

    const changeSupplyDetail = useCallback((supplyDetailIndex, detail) => {

        changeSupplyDetails(
            supplyFormMemo.supplyQuantityDetails.map((supplyDetail, index) =>
                index === supplyDetailIndex ? detail : {...supplyDetail}
            )
        )
    }, [changeSupplyDetails, supplyFormMemo]);

    const addSupplyDetail = useCallback((commodityOwnerCd, remarks) => {

        changeSupplyDetails([
            ...supplyFormMemo.supplyQuantityDetails,
            {
                commodityOwnerCd,
                isValidUnitQuantity: false, supplyQuantity: null, supplyUnitQuantity: null,
                remarks: remarks ?? "",
                isDelete: false
            }
        ])
    }, [changeSupplyDetails, supplyFormMemo]);

    const deleteSupplyDetail = useCallback((supplyDetailIndex) => {
        changeSupplyDetails(
            supplyFormMemo.supplyQuantityDetails.map((supplyDetail, index) => (
                index === supplyDetailIndex
                    ? {...supplyDetail, isDelete: true}
                    : {...supplyDetail}
            ))
        )
    }, [changeSupplyDetails, supplyFormMemo]);

    const supplyStyle = {
        ...(enabledDisplayRemarks
                ? supplyProductStyles.supplyWithRemarks
                : supplyProductStyles.supply
        ),
        ...supplyCellStyles.leftBorder,
    }
    // console.log(supplyForm.originId);

    const handleHideClick = useCallback(() => {
        if (onHideProduct && originalForm) {
            onHideProduct(originalForm);
        }
    }, [onHideProduct, originalForm]);

    const rowRef = useRef(null);
    const dragHandleRef = useRef(null);

    const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
        type: DND_ITEM_TYPES.PRODUCT_ROW,
        item: { product: originalForm, index, originalIndex: index, type: DND_ITEM_TYPES.PRODUCT_ROW },
        end: (item, monitor) => {
            const dropResult = monitor.getDropResult();
            if (dropResult?.cancelled && item.originalIndex !== undefined && item.index !== item.originalIndex) {
                onMoveItem(item.index, item.originalIndex);
            }
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [originalForm, index, onMoveItem]);

    const [{ isOver }, drop] = useDrop(() => ({
        accept: [DND_ITEM_TYPES.PRODUCT_ROW, DND_ITEM_TYPES.HIDDEN_ROW, DND_ITEM_TYPES.ADD_PRODUCT_ROW],
        hover: (draggedItem, monitor) => {
            if (!rowRef.current) return;
            // sidebar からドラッグ中: PRODUCT_ROW の並び替えと同じく、行に触れた瞬間に
            // ghost をその行の手前へ移動させる (= visibleIndex を採用)。次の行に入ると
            // visibleIndex が +1 され ghost が一段ずれる → 「枠に触れたら入れ替わる」UX。
            if (draggedItem.type === DND_ITEM_TYPES.ADD_PRODUCT_ROW) {
                if (onGhostHover && visibleIndex != null) {
                    onGhostHover(visibleIndex, draggedItem);
                }
                return;
            }
            // Only do reorder logic for PRODUCT_ROW
            if (draggedItem.type !== DND_ITEM_TYPES.PRODUCT_ROW) return;

            const dragIndex = draggedItem.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) return;

            if (onMoveItem) {
                onMoveItem(dragIndex, hoverIndex);
            }
            draggedItem.index = hoverIndex;
        },
        drop: (draggedItem) => {
            // ADD_PRODUCT_ROW: 親 drop が ghost.visibleIndex を見て commit するためここでは扱わない。
            // (return undefined → 親 drop が引き続き処理される)
            // HIDDEN_ROW は ghost preview がないので、シンプルに行の手前へ挿入。
            if (draggedItem.type === DND_ITEM_TYPES.HIDDEN_ROW && onShowProductAt) {
                onShowProductAt(draggedItem.product, index);
                return { handled: true };
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
        }),
    }), [index, visibleIndex, onMoveItem, onShowProductAt, onGhostHover]);

    // Separate drag and drop refs
    drop(rowRef);
    drag(dragHandleRef);

    // 並び替え時はリスト自体がリアルタイムで動くので、HTML5 のドラッグゴーストは非表示にする
    useEffect(() => {
        dragPreview(getEmptyImage(), { captureDraggingState: true });
    }, [dragPreview]);

    // Determine background color
    const getBackgroundColor = () => {
        if (isOver || isRecentlyDropped) return "#bbdefb";
        if (supplyForm.isAddedFromHistory) return green[100];
        if (supplyForm.isAddedFromDialog) return red[100];
        return "white";
    };

    return (
        <TableRow
            ref={rowRef}
            sx={{
            verticalAlign: "middle",
            backgroundColor: getBackgroundColor(),
            opacity: 1,
            borderTop: isOver ? "2px solid" : "none",
            borderColor: isOver ? "primary.main" : "transparent",
            transition: "background-color 0.3s ease, opacity 0.15s ease",
        }}>
            <TableCell sx={supplyProductStyles.dragHandle}>
                <div ref={dragHandleRef} style={{ cursor: "grab", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <DragIndicatorIcon sx={{ fontSize: 18, color: "primary.main" }} />
                </div>
            </TableCell>
            <TableCell sx={supplyProductStyles.hideButton}>
                <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={handleHideClick}
                    startIcon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
                    sx={{
                        fontSize: 11,
                        py: 1,
                        px: 3,
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                        color: "grey.500",
                        borderColor: "grey.300",
                        "&:hover": {
                            color: "error.main",
                            borderColor: "error.main",
                            bgcolor: "error.50"
                        }
                    }}
                >
                    非表示
                </Button>
            </TableCell>
            <TableCell sx={supplyProductStyles.productName}>
                {supplyForm.productName}
            </TableCell>
            <TableCell sx={supplyProductStyles.origin}>
                <CommonAutocomplete
                    value={origin}
                    onSelect={(e, newValue) => {
                        console.log(e);
                        onOriginChange(supplyForm.supplyProductIndex, newValue);
                    }}
                    id={`supply-origin-${supplyForm.supplyProductIndex}`}
                    data={origins}
                    filterOptions={(options, {inputValue}) => {
                        inputValue = inputValue.trim();
                        return options.filter((x) => {
                            return x.originId?.toString().startsWith(inputValue);
                            // x.originName.includes(inputValue) - uncomment if needed
                        });
                    }}
                    optionLabel={(option) =>
                        option ? (option.originId || "") + (option.originId? " - " : "") + (option.originName || "") : ""
                    }
                    width={"95%"}
                />
            </TableCell>
            <TableCell sx={supplyProductStyles.size}>
                {supplyForm.displaySize}
            </TableCell>
            <TableCell sx={supplyProductStyles.supplyUnit}>
                {supplyForm.supplyUnitName}
            </TableCell>
            {/*<TableCell sx={supplyProductStyles.order}>*/}
            {/*  <Table>*/}
            {/*    <TableBody>*/}
            {/*      {*/}
            {/*        supplyForm.orderDetails.map((orderDetail, index) => (*/}
            {/*          <TableRow sx={tableInRowStyle} key={`order_detail_${index}`}>*/}
            {/*            <TableCell>*/}
            {/*              <Grid container spacing={1}>*/}
            {/*                <Grid item sx={{...cellStyle, ...orderCellStyles.unitQuantity}}>*/}
            {/*                  {orderDetail.orderingUnitQuantity || ""}*/}
            {/*                </Grid>*/}
            {/*                <Grid item sx={{...cellStyle, ...orderCellStyles.orderAmount, textAlign: "right"}}>*/}
            {/*                  {orderDetail.orderQuantity || 0}*/}
            {/*                </Grid>*/}
            {/*              </Grid>*/}
            {/*            </TableCell>*/}
            {/*          </TableRow>*/}
            {/*        ))          */}
            {/*      }*/}
            {/*    </TableBody>*/}
            {/*  </Table>*/}
            {/*</TableCell>*/}
            {/*<TableCell sx={supplyProductStyles.totalSupplyAmount}>*/}
            {/*  {supplyForm.totalSupplyAmount}*/}
            {/*</TableCell>*/}
            {
                suppliers.map((supplier, supplierIndex) => (
                        <TableCell
                            sx={supplyStyle}
                            key={`supply_detail_${supplier.commodityOwnerName}`}
                        >
                            <SupplyDetailTable
                                supplyQuantityDetails={
                                    supplyForm.supplyQuantityDetails
                                        .filter((supplyDetail) =>
                                            !supplyDetail.isDelete
                                        )
                                }
                                changeSupplyDetail={changeSupplyDetail}
                                addSupplyDetail={addSupplyDetail}
                                deleteSupplyDetail={deleteSupplyDetail}
                                enabledDisplayRemarks={enabledDisplayRemarks}
                                keyHandler={keyHandler(supplierIndex)}
                            />
                        </TableCell>
                    )
                )
            }
        </TableRow>
    )
}

export default React.memo(SupplyTableRow);

